if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
        };

        this.submitEl.addEventListener('click', this.onClick.bind(this));
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        // Observe media gallery instead of product-info for earlier trigger
        this.productMedia = document.querySelector('.product__media-gallery, .product-media-gallery, [data-product-media-container]');
        this.productId = this.dataset.productId;
        this.dismissedKey = `sticky-atc-dismissed-${this.productId}`;

        // Check if user has dismissed this sticky bar
        if (sessionStorage.getItem(this.dismissedKey)) {
          this.style.display = 'none';
          return;
        }

        this.init();
        this.initCloseButton();

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        // Prefer media gallery, fallback to product-info
        const observeTarget = this.productMedia || this.mainProductInfo;

        if (!observeTarget) {
          this.classList.remove(this.classes.isOpen);
          return;
        }

        // Show sticky bar when product media scrolls out of view
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle(this.classes.isOpen, !entry.isIntersecting);
            });
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(observeTarget);
        this.syncWithMainProductForm();
      }

      initCloseButton() {
        const closeButton = this.querySelector('.sticky-atc-bar__close');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            this.dismiss();
          });
        }
      }

      dismiss() {
        // Store dismissal in sessionStorage (persists for page session)
        sessionStorage.setItem(this.dismissedKey, 'true');

        // Hide the bar
        this.classList.remove(this.classes.isOpen);
        this.style.display = 'none';

        // Disconnect observer since we no longer need it
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        const scrollIntoView = (selector, offset) => {
          window.scrollTo({
            behavior: 'smooth',
            top: selector.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
          });
        };

        scrollIntoView(target, offset);
      }

      updateButton(disable = true, text, _modifyClass = true) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = variant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      getSizedImageUrl(url, width) {
        if (!url) return '';
        // Handle Shopify CDN URLs with width parameter
        if (url.includes('/cdn/shop/') || url.includes('cdn.shopify.com')) {
          // Remove existing width parameter if present
          const baseUrl = url.replace(/[?&]width=\d+/g, '').replace(/\?$/, '');
          const separator = baseUrl.includes('?') ? '&' : '?';
          return `${baseUrl}${separator}width=${width}`;
        }
        return url;
      }

      updateImage(variant) {
        const imageContainer = this.querySelector('.sticky-atc-bar__product-image');
        if (!imageContainer) return;

        const img = imageContainer.querySelector('img');
        if (!img) return;

        if (!variant) return;

        // Priority 1: Check for custom media config (from variant metafields)
        const customMediaConfig = this.getCustomMediaConfig(variant);
        if (customMediaConfig && customMediaConfig.hasCustomMedia && customMediaConfig.mediaObjects && customMediaConfig.mediaObjects.length > 0) {
          const newSrc = customMediaConfig.mediaObjects[0].external_url;
          if (newSrc) {
            const srcset = `${this.getSizedImageUrl(newSrc, 70)} 70w, ${this.getSizedImageUrl(newSrc, 140)} 140w`;

            img.src = this.getSizedImageUrl(newSrc, 180);
            img.srcset = srcset;
            img.alt = variant.title || '';
            return;
          }
        }

        // Priority 2: Fallback to variant's featured_image
        if (variant.featured_image) {
          const newSrc = variant.featured_image.src;
          const srcset = `${this.getSizedImageUrl(newSrc, 70)} 70w, ${this.getSizedImageUrl(newSrc, 140)} 140w`;

          img.src = this.getSizedImageUrl(newSrc, 180);
          img.srcset = srcset;
          img.alt = variant.featured_image.alt || variant.title || '';
          img.width = variant.featured_image.width || 180;
          img.height = variant.featured_image.height || 180;
        }
      }

      getCustomMediaConfig(variant) {
        if (!variant || !variant.id) return null;

        const configElement = document.getElementById(`custom-media-config-${variant.id}`);
        if (!configElement) return null;

        try {
          return JSON.parse(configElement.textContent);
        } catch (error) {
          console.warn('Failed to parse custom media config:', error);
          return null;
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.mainProductInfo) return;
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          if (!variant) {
            this.updateButton(true, '', true);
            return;
          }

          this.updateImage(variant);
          this.updatePrice(variant);
          this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
        });
      }
    }
  );
}
